'use client';

import { useState, useCallback } from 'react';
import { cn } from '@/lib/utils';

interface VoteButtonProps {
  requestId: string;
  portalSlug: string;
  initialCount: number;
  initialHasVoted: boolean;
  onAuthRequired?: () => void;
}

export function VoteButton({
  requestId,
  portalSlug,
  initialCount,
  initialHasVoted,
  onAuthRequired,
}: VoteButtonProps) {
  const [count, setCount] = useState(initialCount);
  const [hasVoted, setHasVoted] = useState(initialHasVoted);
  const [isLoading, setIsLoading] = useState(false);

  const handleVote = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (isLoading) return;

      // Optimistic update
      const previousCount = count;
      const previousHasVoted = hasVoted;
      setHasVoted(!hasVoted);
      setCount(hasVoted ? count - 1 : count + 1);
      setIsLoading(true);

      try {
        const method = hasVoted ? 'DELETE' : 'POST';
        const res = await fetch(
          `/api/portal/${portalSlug}/requests/${requestId}/vote`,
          { method }
        );

        if (res.status === 401) {
          // Revert optimistic update
          setCount(previousCount);
          setHasVoted(previousHasVoted);
          onAuthRequired?.();
          return;
        }

        if (!res.ok) {
          // Revert on error
          setCount(previousCount);
          setHasVoted(previousHasVoted);
          return;
        }

        const data = (await res.json()) as { voteCount?: number };
        if (data.voteCount !== undefined) {
          setCount(data.voteCount);
        }
      } catch {
        // Revert on network error
        setCount(previousCount);
        setHasVoted(previousHasVoted);
      } finally {
        setIsLoading(false);
      }
    },
    [count, hasVoted, isLoading, portalSlug, requestId, onAuthRequired]
  );

  return (
    <button
      onClick={handleVote}
      disabled={isLoading}
      className={cn(
        'flex flex-col items-center justify-center gap-0.5 rounded-lg border px-3 py-2 transition-all duration-200 min-w-[52px]',
        hasVoted
          ? 'border-primary bg-primary/10 text-primary shadow-sm hover:bg-primary/15'
          : 'border-border bg-card text-muted-foreground hover:border-primary/50 hover:text-primary hover:scale-105'
      )}
      aria-label={hasVoted ? 'Remove vote' : 'Upvote'}
    >
      <svg
        width="16"
        height="10"
        viewBox="0 0 16 10"
        fill="none"
        className={cn(
          'transition-all duration-200',
          hasVoted ? 'text-primary' : 'text-current'
        )}
      >
        <path
          d="M8 0L15 9H1L8 0Z"
          fill="currentColor"
        />
      </svg>
      <span className="text-sm font-semibold leading-tight">{count}</span>
    </button>
  );
}
